import { MoreVertical } from "lucide-react"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { Button } from "~/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/ui/dropdown-menu"
import { useToast } from "~/ui/use-toast"
import { InvitationStatus } from "~/__generated__/graphql"
import { updateInvitationMutation } from "~/invitations/invitations-screen"

interface InvitationActionsDropdownProps {
  invitationId: string
  currentStatus: InvitationStatus
}

export function InvitationActionsDropdown({
  invitationId,
  currentStatus,
}: InvitationActionsDropdownProps) {
  const { toast } = useToast()
  const [updateInvitation, { loading }] = useSafeMutation(updateInvitationMutation)

  const handleExpireInvitation = async (e: React.MouseEvent) => {
    e.stopPropagation()

    const confirmed = window.confirm(
      "Are you sure you want to expire this invitation? This will make the invitation link inactive."
    )

    if (!confirmed) return

    try {
      const result = await updateInvitation({
        variables: {
          input: {
            id: invitationId,
            invitationInput: {
              status: InvitationStatus.Expired,
            },
          },
        },
      })

      if (result.data?.invitationUpdate?.invitation) {
        toast({
          title: "Invitation expired",
          description: "The invitation has been expired and is no longer active.",
        })
      }
    } catch (error) {
      console.error("Error expiring invitation:", error)
      toast({
        title: "Error",
        description: "Failed to expire invitation. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleActivateInvitation = async (e: React.MouseEvent) => {
    e.stopPropagation()

    try {
      const result = await updateInvitation({
        variables: {
          input: {
            id: invitationId,
            invitationInput: {
              status: InvitationStatus.Active,
            },
          },
        },
      })

      if (result.data?.invitationUpdate?.invitation) {
        toast({
          title: "Invitation activated",
          description: "The invitation is now active and can be used.",
        })
      }
    } catch (error) {
      console.error("Error activating invitation:", error)
      toast({
        title: "Error",
        description: "Failed to activate invitation. Please try again.",
        variant: "destructive",
      })
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreVertical className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {currentStatus === InvitationStatus.Active ? (
          <DropdownMenuItem
            onClick={handleExpireInvitation}
            disabled={loading}
            className="text-red-600"
          >
            {loading ? "Expiring..." : "Expire Invitation"}
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={handleActivateInvitation} disabled={loading}>
            {loading ? "Activating..." : "Activate Invitation"}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
